import { useRef } from 'react'

import { cardCartKeys } from '@/components/quotes/quoteCartItems'
import { cn } from '@/lib/utils'
import { ComparisonViewer, PANEL_READ_POINTS } from './ComparisonViewer'
import { CursorTour } from './CursorTour'
import { OpeningScene } from './OpeningScene'
import { ANCHOR_INDEX, ANIMATION_QUOTES, COMPARED_INDEX } from './quoteAnimationData'
import { useCtaPosition } from './useCtaPosition'
import { Scene, useQuoteAnimation } from './useQuoteAnimation'
import { STAGE_WIDTH, useStageScale } from './useStageScale'

const EMPTY_CART: ReadonlySet<string> = new Set()

const ANCHOR = ANIMATION_QUOTES[ANCHOR_INDEX]
const COMPARED = ANIMATION_QUOTES[COMPARED_INDEX]
const ANCHOR_CART_KEYS: ReadonlySet<string> = new Set(cardCartKeys(ANCHOR))

const DESCRIPTION =
  'An animation of the Cai quotes screen: six carrier quotes side by side, two opened for comparison, and one added to the cart.'

interface QuoteComparisonAnimationProps {
  className?: string
}

/**
 * Marketing loop of the quotes screen, built from the product's own QuoteCard, focus
 * reveal and comparison rail rather than a mock-up — so it can't drift from the real UI.
 *
 * Decorative: the stage is inert and hidden from assistive tech, and none of the cards'
 * real handlers are wired, so nothing here reports an analytics event.
 */
export function QuoteComparisonAnimation({ className }: QuoteComparisonAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const { scene, isOpen, isArmed, showsSecondCard, showsCursor, isTouring, isPressed, isInCart } =
    useQuoteAnimation(containerRef)
  const { scale, stageHeight } = useStageScale(containerRef, stageRef)
  const cursorPosition = useCtaPosition(anchorRef, showsCursor, isInCart)

  const isClosing = scene === Scene.CLOSING
  // Kept mounted through the close so it fades out; unmounting on ROW replays the open beats.
  const showsViewer = isOpen || isClosing
  // The return to the row is quicker than the open — the payoff has already landed.
  const crossfade = isClosing ? 'duration-300' : 'duration-500'

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full overflow-hidden', className)}
      style={{ aspectRatio: `${STAGE_WIDTH} / ${stageHeight}` }}
      role="img"
      aria-label={DESCRIPTION}
    >
      <div
        ref={stageRef}
        inert
        className="absolute top-0 left-1/2 grid"
        style={{
          width: STAGE_WIDTH,
          minHeight: stageHeight,
          transform: `translateX(-50%) scale(${scale})`,
          transformOrigin: 'top center',
        }}
      >
        <div
          className={cn(
            'relative col-start-1 row-start-1 transition-[opacity,scale] ease-out',
            crossfade,
            isOpen ? 'scale-105 opacity-0' : 'scale-100 opacity-100'
          )}
        >
          <OpeningScene quotes={ANIMATION_QUOTES} armedIndex={ANCHOR_INDEX} armed={isArmed} />
        </div>

        <div
          className={cn(
            'col-start-1 row-start-1 transition-[opacity,scale] ease-out',
            crossfade,
            isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          )}
        >
          {showsViewer && (
            <ComparisonViewer
              quotes={ANIMATION_QUOTES}
              anchor={ANCHOR}
              compared={COMPARED}
              showsSecondCard={showsSecondCard}
              cartKeys={isInCart ? ANCHOR_CART_KEYS : EMPTY_CART}
              anchorRef={anchorRef}
              cursor={
                showsCursor && (
                  <CursorTour
                    columnsX={PANEL_READ_POINTS}
                    ctaPosition={cursorPosition}
                    touring={isTouring}
                    pressed={isPressed}
                  />
                )
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}
